import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import type { Maybe } from '@xing-com/crate-common-graphql-types';
import type { FlagVariant } from '@xing-com/flag';
import { Flag } from '@xing-com/flag';
import { ProfileImage } from '@xing-com/profile-image';
import { BodyCopy } from '@xing-com/typography';

import { getFlagVariant } from '../../helpers';
import type { ArticleAuthorType } from '../../types';

import { Styled } from './styles';

export interface ArticlePaywallProps {
  membership: Extract<FlagVariant, 'premium' | 'proJobs'>;
  articleTitle: string;
  author?: Maybe<ArticleAuthorType>;
  topOffset?: number;
}

export const getPaywallUrl = (
  membership: Extract<FlagVariant, 'premium' | 'proJobs'>
): string => {
  switch (membership) {
    case 'premium':
      return '/purchase/premium?reagent=uplt_1025';
    case 'proJobs':
      return '/purchase/projobs?reagent=uplt_1028';
    default:
      return '';
  }
};

const ArticlePaywall: React.FC<ArticlePaywallProps> = ({
  membership,
  articleTitle,
  author,
  topOffset = 0,
}) => {
  return (
    <Styled.ArticlePaywallContainer
      data-qa="article-viewer-membership-paywall"
      topOffset={topOffset}
    >
      <Styled.PaywallCard maxWidth="650px">
        {author && (
          <Styled.AuthorProfile>
            <ProfileImage size="small" src={author?.profileImage || ''} />
            <div>
              <Styled.ArticleAuthorTitle>
                <Styled.ArticleAuthorLink href={author?.pageURL}>
                  <Styled.ArticleAuthorName size="small">
                    {author?.title}
                  </Styled.ArticleAuthorName>
                </Styled.ArticleAuthorLink>
                {author?.userFlag ? (
                  <Flag
                    variant={getFlagVariant(author.userFlag)}
                    data-qa="article-author-user-flag"
                    size="small"
                  />
                ) : null}
              </Styled.ArticleAuthorTitle>
              <Styled.ArticleHeadline size="large">
                {articleTitle}
              </Styled.ArticleHeadline>
            </div>
          </Styled.AuthorProfile>
        )}
        <Styled.InnerCard size="small" variant={2}>
          <Flag
            data-qa="article-paywall-membership-flag"
            variant={membership}
          />
          <Styled.InnerCardHeadline size="small">
            <FormattedMessage
              id={`ARTICLES_${membership.toUpperCase()}_BANNER_HEADLINE`}
            />
          </Styled.InnerCardHeadline>
          <BodyCopy>
            <FormattedMessage
              id={`ARTICLES_${membership.toUpperCase()}_BANNER_BODY`}
            />
          </BodyCopy>
          <Button variant={membership} to={getPaywallUrl(membership)}>
            <FormattedMessage
              id={`ARTICLES_${membership.toUpperCase()}_BANNER_BUTTON`}
            />
          </Button>
        </Styled.InnerCard>
      </Styled.PaywallCard>
    </Styled.ArticlePaywallContainer>
  );
};

export default ArticlePaywall;
